import {useQuery} from "@apollo/client";
import {useEffect, useState} from "react";
import {useParams, useSearchParams} from "react-router";
import {useDebounce} from "use-debounce";

import {ActionBarWrapper} from "@/components/actionBarWrapper/ActionBarWrapper.tsx";
import {FilterCategory, FilterCategoryType} from "@/components/filterDropdown/FilterDropdown.ts";
import {FilterDropdown} from "@/components/filterDropdown/FilterDropdown.tsx";
import {SearchInput} from "@/components/searchInput/SearchInput.tsx";
import {usePagination} from "@/lib/hooks/usePagination.ts";
import {GQL_CREDENTIALS_QUERY} from "@/lib/queries/credentials.ts";
import {ResponseStatus} from "@/lib/types/common.ts";
import {CredentialListResponse, Credentials, CredentialTypeLabel} from "@/lib/types/credentials.ts";

import {CredentialUpdateModal} from "./vaultForm/CredentialUpdateModal.tsx";
import {VaultTable} from "./VaultTable.tsx";

const filterFields = [
  {
    key: "credential_type",
    type: FilterCategoryType.ARRAY,
    display: "Credential Type",
    options: Object.keys(CredentialTypeLabel).map((key) => ({
      key,
      display: CredentialTypeLabel[key as keyof typeof CredentialTypeLabel],
    })),
    sorted: true,
  },
] satisfies FilterCategory[];

export const VaultList = () => {
  const [searchParams] = useSearchParams();
  const credentialId = searchParams.get("credentialId");

  const {searchQueryParam} = useParams();
  const [search, setSearch] = useState(searchQueryParam);
  const [searchDebounced] = useDebounce(search, 300);
  const [credentialEditId, setCredentialEditId] = useState<string | null>(credentialId);
  const [showUpdateCredentialModal, setShowUpdateCredentialModal] = useState(!!credentialId);
  const [credentialTypeFilter, setCredentialTypeFilter] = useState<string[] | null>(null);
  const {pagination, setPagination} = usePagination({useUrlParams: false});
  const {loading, data, previousData} = useQuery<CredentialListResponse>(GQL_CREDENTIALS_QUERY, {
    variables: {
      search: searchDebounced ? searchDebounced : undefined,
      credential_types: credentialTypeFilter ? credentialTypeFilter : undefined,
      ...pagination,
    },
  });
  let credentials: Credentials[] = [];
  let credentialsTotal = 0;

  useEffect(() => {
    setPagination({...pagination, offset: 0}); // eslint-disable-next-line react-hooks/exhaustive-deps -- only reset offset when filters change
  }, [searchDebounced, credentialTypeFilter]);

  const currentData = loading ? previousData : data;

  if (currentData?.response?.status === ResponseStatus.SUCCESS) {
    credentials = currentData.response.data;
    credentialsTotal = currentData.response.total || 0;
  }

  const handleLinkClick = (id: string) => {
    setCredentialEditId(id);
    setShowUpdateCredentialModal(true);
  };

  return (
    <div>
      <CredentialUpdateModal
        show={showUpdateCredentialModal}
        setShow={setShowUpdateCredentialModal}
        credentialId={credentialEditId ? credentialEditId : ""}
      />
      <ActionBarWrapper
        className="mb-3"
        left={
          <>
            <SearchInput
              onChange={(value) => {
                setSearch(value);
                setPagination({...pagination, offset: 0});
              }}
              value={search || ""}
              variant={"tableControlBar"}
            />
            <FilterDropdown
              categories={filterFields}
              appliedFilterSets={{
                credential_type: credentialTypeFilter ? credentialTypeFilter : [],
              }}
              onApply={(updatedCategories) => {
                setCredentialTypeFilter(updatedCategories.credential_type ? updatedCategories.credential_type : null);
              }}
            />
          </>
        }
      />
      <VaultTable
        credentials={credentials}
        handleIdClick={handleLinkClick}
        offset={pagination.offset}
        limit={pagination.limit}
        setLaf={setPagination}
        total={credentialsTotal}
        dataLoading={loading}
      />
    </div>
  );
};
